import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PencilLine, X } from 'lucide-react';
import Button from '../../../components/commoncomponents/Button';

function BusinessRenameModal({ business, isOpen, onClose, onSave, saving, error }) {
  const [businessName, setBusinessName] = useState('');

  useEffect(() => {
    if (isOpen && business) {
      setBusinessName(business.name || '');
    }
  }, [business, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !business) {
    return null;
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSave(businessName.trim());
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="max-h-[calc(100dvh-3rem)] w-full max-w-[500px] overflow-y-auto rounded-[28px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-4">
                <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[18px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
                  <PencilLine className="h-[22px] w-[22px]" />
                </span>
                <div className="min-w-0">
                  <h3 className="m-0 text-[1.35rem] font-semibold tracking-[-0.03em] text-[#111111]">
                    Rename Business
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-[#7f736b]">
                    Update the name shown on this business card.
                  </p>
                </div>
              </div>
            </div>
            <button
              aria-label="Close rename business modal"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5dfd8] bg-white text-[#7f736b] shadow-[0_10px_24px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <form className="grid gap-4 px-5 py-5 sm:px-6" onSubmit={handleSubmit}>
          <label className="grid gap-2.5 rounded-[20px] border border-[#e6ddd4] bg-white px-4 py-4">
            <span className="text-sm font-semibold text-[#111111]">Business Name</span>
            <input
              autoFocus
              className="rounded-[14px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
              type="text"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              placeholder="Enter business name"
            />
          </label>

          {error ? (
            <div className="rounded-[18px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f]">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              className="w-full justify-center rounded-[16px] px-5 py-3 sm:w-auto"
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="w-full justify-center rounded-[16px] px-5 py-3 sm:w-auto"
              type="submit"
              disabled={saving || !businessName.trim()}
            >
              {saving ? 'Saving...' : 'Save Name'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default BusinessRenameModal;
